import "./CreateTrialModal.scss";

import React, { useState } from "react";
import {
  TrialLesson,
  useTrialLesson,
} from "../../../context/TrailLessonContext";

import { ClipLoader } from "react-spinners";
import { X } from "lucide-react";
import { toast } from "sonner";
import useLanguages from "../../../context/LanguagesContext";

interface IModalProps {
  handleCloseModal: (val: boolean) => void;
}

export interface Language {
  language: string;
  proficiency: string;
  status: string;
}

export interface ApprovedLanguagesResponse {
  statusCode: number;
  status: string;
  message: string;
  result: Language[];
}

const initialData: TrialLessonFormData = {
  languageId: 0,
  description: "",
  price: 2,
  duration: 30,
};

type TrialLessonFormData = Omit<TrialLesson, "type" | "name">;

const CreateTrialModal = ({ handleCloseModal }: IModalProps) => {
  const { languages_taught: supported_languages } = useLanguages();
  const { saveLesson, teacherLanguagesArr } = useTrialLesson();

  const [lessonData, setLessonData] =
    useState<TrialLessonFormData>(initialData);

  const [isPending, setIsPending] = useState(false);

  const filterApprovedTeacherLanguages = supported_languages.filter((lang) =>
    teacherLanguagesArr.some((t) => t.language === lang.name),
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setLessonData((prev) => ({
      ...prev,
      [name]:
        name === "languageId" || name === "price" || name === "duration"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPending(true);

    try {
      const data = await saveLesson({
        ...lessonData,
        name: "Trial Lesson",
        type: "trial",
      });
      toast.success("Trial Lesson created successfully.", { richColors: true });
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message ?? "Error creating trial lesson");
    } finally {
      handleCloseModal(true);
      setIsPending(false);
    }
  };

  const clearHandler = () => {
    setLessonData(initialData);
  };

  const closeTrialModel = () => {
    handleCloseModal(true);
  };

  return (
    <div className="createTrialModal">
      <form onSubmit={handleSubmit} className="modalForm">
        <h2 className="sectionTitle">Create Trial Lesson</h2>
        <X className="closeModal" onClick={closeTrialModel} />
        <div className="formGrid">
          <div className="formSection">
            <label className="formLabel">
              Lesson Title
              <input
                type="text"
                name="name"
                // placeholder=""
                value="Trail Lesson"
                disabled
                onChange={handleChange}
              />
            </label>

            <label className="formLabel">
              Lesson Description
              <p className="inputSubtitle">
                What do students expect to learn from this lesson.
              </p>
              <textarea
                name="description"
                required
                placeholder="Lesson Description"
                value={lessonData.description}
                onChange={handleChange}
              />
            </label>

            <label className="formLabel">
              Select your language
              <select
                name="languageId"
                value={lessonData.languageId || ""}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Option
                </option>
                {filterApprovedTeacherLanguages &&
                  filterApprovedTeacherLanguages.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <div className="formSection">
            <div className="packageGroup">
              <label className="formLabel">
                Price(USD)
                <input
                  type="number"
                  disabled
                  name="price"
                  placeholder="e.g. 4"
                  value={lessonData.price}
                  onChange={handleChange}
                  className="packageInput"
                />
              </label>

              <label className="formLabel">
                Duration(Mins)
                <input
                  type="number"
                  name="duration"
                  disabled
                  placeholder="30min"
                  value={lessonData.duration}
                  onChange={handleChange}
                  className="packageInput"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="modalActions">
          <button
            type="button"
            onClick={clearHandler}
            className="discardButton"
          >
            Discard
          </button>
          <button type="submit" className="saveButton" disabled={isPending}>
            {isPending ? (
              <span>
                <ClipLoader color="white" size={15} /> Saving
              </span>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTrialModal;
